import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import "./App.css";

// Pages
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import ExamSelection from "./pages/ExamSelection";
import DisciplineSelection from "./pages/DisciplineSelection";
import Practice from "./pages/Practice";
import MockExam from "./pages/MockExam";
import Flashcards from "./pages/Flashcards";
import AITutor from "./pages/AITutor";
import Progress from "./pages/Progress";

// API Setup
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

// Axios instance with auth
export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    
    if (token && savedUser) {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
      } catch (err) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  };

  const login = async (sessionId) => {
    try {
      const res = await axios.post(`${API}/auth/google`, { session_id: sessionId });
      const { token, user: userData } = res.data;
      
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
      
      return true;
    } catch (err) {
      console.error("Login error:", err);
      toast.error("Login failed. Please try again.");
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

// Auth Callback Handler - Handles session_id in URL hash
function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const location = useLocation();
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    if (processed) return;
    setProcessed(true);
    handleCallback();
  }, []);

  const handleCallback = async () => {
    // Extract session_id from hash (format: #session_id=xxx)
    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;

    if (sessionId) {
      const success = await login(sessionId);
      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname);
      if (success) {
        toast.success("Welcome to LearnVault!");
        navigate("/dashboard", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } else {
      navigate("/", { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
}

// App Router - Check for session_id synchronously to prevent race conditions
function AppRouter() {
  const location = useLocation();
  
  // Check URL hash for session_id BEFORE routing (synchronous check prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/exam-selection" element={<ProtectedRoute><ExamSelection /></ProtectedRoute>} />
      <Route path="/disciplines/:examType" element={<ProtectedRoute><DisciplineSelection /></ProtectedRoute>} />
      <Route path="/practice/:examType/:discipline" element={<ProtectedRoute><Practice /></ProtectedRoute>} />
      <Route path="/mock-exam/:examType/:discipline" element={<ProtectedRoute><MockExam /></ProtectedRoute>} />
      <Route path="/flashcards/:examType/:discipline" element={<ProtectedRoute><Flashcards /></ProtectedRoute>} />
      <Route path="/ai-tutor" element={<ProtectedRoute><AITutor /></ProtectedRoute>} />
      <Route path="/progress" element={<ProtectedRoute><Progress /></ProtectedRoute>} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Main App
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
